import React from 'react'
import { AlertCircle, Image, Loader2, Trash2, Calendar, ChevronDown } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import ErrorBanner from '@/components/ErrorBanner'
import { type Photo } from '@/services/turbineService'
import { getRoomDisplayName, getMonthName } from '../helpers'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface AllPhotosSectionProps {
  canDeletePhotos: VM['canDeletePhotos']
  photosLoading: VM['photosLoading']
  availableYears: VM['availableYears']
  filteredPhotosBySearch: VM['filteredPhotosBySearch']
  setModalPhoto: VM['setModalPhoto']
  handleDeletePhotoFromList: VM['handleDeletePhotoFromList']
  isBulkDeleteMode: VM['isBulkDeleteMode']
  toggleBulkDeleteMode: VM['toggleBulkDeleteMode']
  selectedPhotosForBulkDelete: VM['selectedPhotosForBulkDelete']
  togglePhotoForBulkDelete: VM['togglePhotoForBulkDelete']
  handleBulkDelete: VM['handleBulkDelete']
  isDeletingBulk: VM['isDeletingBulk']
  isComparisonMode: VM['isComparisonMode']
  toggleComparisonMode: VM['toggleComparisonMode']
  selectedPhotosForComparison: VM['selectedPhotosForComparison']
  togglePhotoSelection: VM['togglePhotoSelection']
  openComparisonModal: VM['openComparisonModal']
  searchTerm: VM['searchTerm']
  setSearchTerm: VM['setSearchTerm']
  allPhotosSelectedYear: VM['allPhotosSelectedYear']
  setAllPhotosSelectedYear: VM['setAllPhotosSelectedYear']
  allPhotosSelectedWeek: VM['allPhotosSelectedWeek']
  setAllPhotosSelectedWeek: VM['setAllPhotosSelectedWeek']
  selectedPhotoType: VM['selectedPhotoType']
  setSelectedPhotoType: VM['setSelectedPhotoType']
  weekOptions: VM['weekOptions']
  isAllPhotosYearDropdownOpen: VM['isAllPhotosYearDropdownOpen']
  setIsAllPhotosYearDropdownOpen: VM['setIsAllPhotosYearDropdownOpen']
  isAllPhotosWeekDropdownOpen: VM['isAllPhotosWeekDropdownOpen']
  setIsAllPhotosWeekDropdownOpen: VM['setIsAllPhotosWeekDropdownOpen']
  isTypeDropdownOpen: VM['isTypeDropdownOpen']
  setIsTypeDropdownOpen: VM['setIsTypeDropdownOpen']
  allPhotosYearDropdownRef: VM['allPhotosYearDropdownRef']
  allPhotosWeekDropdownRef: VM['allPhotosWeekDropdownRef']
  typeDropdownRef: VM['typeDropdownRef']
  /** Fotograf listesi yuklenemediginde dolu gelir. "Kayit yok" ile KARISTIRILMAZ. */
  photosLoadError: VM['photosLoadError']
  retryLoadPhotos: VM['retryLoadPhotos']
  /** Toplu silmede basarisiz olanlarin adlari + nedenleri. */
  bulkDeleteError: VM['bulkDeleteError']
  setBulkDeleteError: VM['setBulkDeleteError']
}

const AllPhotosSection: React.FC<AllPhotosSectionProps> = ({
  canDeletePhotos,
  photosLoading,
  availableYears,
  filteredPhotosBySearch,
  setModalPhoto,
  handleDeletePhotoFromList,
  isBulkDeleteMode,
  toggleBulkDeleteMode,
  selectedPhotosForBulkDelete,
  togglePhotoForBulkDelete,
  handleBulkDelete,
  isDeletingBulk,
  isComparisonMode,
  toggleComparisonMode,
  selectedPhotosForComparison,
  togglePhotoSelection,
  openComparisonModal,
  searchTerm,
  setSearchTerm,
  allPhotosSelectedYear,
  setAllPhotosSelectedYear,
  allPhotosSelectedWeek,
  setAllPhotosSelectedWeek,
  selectedPhotoType,
  setSelectedPhotoType,
  weekOptions,
  isAllPhotosYearDropdownOpen,
  setIsAllPhotosYearDropdownOpen,
  isAllPhotosWeekDropdownOpen,
  setIsAllPhotosWeekDropdownOpen,
  isTypeDropdownOpen,
  setIsTypeDropdownOpen,
  allPhotosYearDropdownRef,
  allPhotosWeekDropdownRef,
  typeDropdownRef,
  photosLoadError,
  retryLoadPhotos,
  bulkDeleteError,
  setBulkDeleteError,
}) => {
  const { t, language } = useLanguage()

  return (
    <>
          {/* All Photos Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6 mt-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                    {/* Title and Filters Row */}
                    <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900 mr-2">Tüm Fotoğraflar</h2>

                {/* Filter Chips Container */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Year Filter */}
                  <div className="relative" ref={allPhotosYearDropdownRef}>
                    <button
                      onClick={() => setIsAllPhotosYearDropdownOpen(!isAllPhotosYearDropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        allPhotosSelectedYear !== 0
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{allPhotosSelectedYear === 0 ? 'Tüm Yıllar' : allPhotosSelectedYear}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isAllPhotosYearDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isAllPhotosYearDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[180px]">
                        <button
                          onClick={() => {
                            setAllPhotosSelectedYear(0)
                            setIsAllPhotosYearDropdownOpen(false)
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg transition-colors ${
                            allPhotosSelectedYear === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Tüm Yıllar
                        </button>
                        {availableYears.map(year => (
                          <button
                            key={year}
                            onClick={() => {
                              setAllPhotosSelectedYear(year)
                              setIsAllPhotosYearDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 last:rounded-b-lg transition-colors ${
                              allPhotosSelectedYear === year ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Week Filter */}
                  <div className="relative" ref={allPhotosWeekDropdownRef}>
                    <button
                      onClick={() => setIsAllPhotosWeekDropdownOpen(!isAllPhotosWeekDropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        allPhotosSelectedWeek !== 0
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{allPhotosSelectedWeek === 0 ? 'Tüm Haftalar' : `${allPhotosSelectedWeek}. Hafta`}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isAllPhotosWeekDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isAllPhotosWeekDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[180px]">
                        <button
                          onClick={() => {
                            setAllPhotosSelectedWeek(0)
                            setIsAllPhotosWeekDropdownOpen(false)
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg transition-colors ${
                            allPhotosSelectedWeek === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Tüm Haftalar
                        </button>
                        {weekOptions.map(week => (
                          <button
                            key={week}
                            onClick={() => {
                              setAllPhotosSelectedWeek(week)
                              setIsAllPhotosWeekDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 last:rounded-b-lg transition-colors ${
                              allPhotosSelectedWeek === week ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {week}. Hafta
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Type Filter */}
                  <div className="relative" ref={typeDropdownRef}>
                    <button
                      onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedPhotoType !== 'all'
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>
                        {selectedPhotoType === 'all' ? 'Tüm Tipler' :
                         selectedPhotoType === 'blade' ? 'Kanat Fotoğrafları' :
                         selectedPhotoType === 'drone' ? 'Drone Fotoğrafları' :
                         selectedPhotoType === 'operator' ? 'Operatör Fotoğrafları' :
                         selectedPhotoType === 'robot' ? 'Robot Fotoğrafları' : 'Tüm Tipler'}
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isTypeDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[220px]">
                        {[
                          { value: 'all', label: 'Tüm Tipler' },
                          { value: 'blade', label: 'Kanat Fotoğrafları' },
                          { value: 'drone', label: 'Drone Fotoğrafları' },
                          { value: 'operator', label: 'Operatör Fotoğrafları' },
                          { value: 'robot', label: 'Robot Fotoğrafları' }
                        ].map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSelectedPhotoType(option.value)
                              setIsTypeDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                              selectedPhotoType === option.value ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Fotoğraf ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">
                  {filteredPhotosBySearch.length} fotoğraf
                </span>

                {/* Bulk Delete Mode Toggle - Only for users with delete permission */}
                {canDeletePhotos && (
                  <button
                    onClick={toggleBulkDeleteMode}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isBulkDeleteMode
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isBulkDeleteMode ? 'Seçimi İptal Et' : 'Seç'}
                  </button>
                )}

                {/* Bulk Delete Button - Show when photos are selected */}
                {isBulkDeleteMode && selectedPhotosForBulkDelete.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={isDeletingBulk}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isDeletingBulk ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Siliniyor...</span>
                      </>
                    ) : (
                      <span>Seçilenleri Sil ({selectedPhotosForBulkDelete.size})</span>
                    )}
                  </button>
                )}

                {/* Comparison Mode Toggle - Only show when not in bulk delete mode */}
                {!isBulkDeleteMode && (
                  <button
                    onClick={toggleComparisonMode}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isComparisonMode
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isComparisonMode ? 'Karşılaştırmayı İptal Et' : 'Karşılaştır'}
                  </button>
                )}

                {/* Compare Button - only show when 2 photos selected */}
                {isComparisonMode && selectedPhotosForComparison.length === 2 && (
                  <button
                    onClick={openComparisonModal}
                    className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                  >
                    2 Fotoğrafı Karşılaştır
                  </button>
                )}
              </div>
            </div>

            {/* Toplu silmede tek tek hangi fotografin neden silinemedigi burada yazar;
                toast'a sigmayacak kadar uzun oldugu icin baglam ici bant kullanilir. */}
            {bulkDeleteError && (
              <ErrorBanner
                message={bulkDeleteError}
                onDismiss={() => setBulkDeleteError(null)}
                className="mb-4"
              />
            )}

            {/* Liste yuklenemediyse ama elimizde ONCEKI veri varsa: veriyi koruyup
                ustune uyari bandi koyariz (Kural 4). Veri hic yoksa asagidaki
                "yuklenemedi" durumu, "fotograf yok" bos durumunun YERINE gecer. */}
            {photosLoadError && filteredPhotosBySearch.length > 0 && (
              <ErrorBanner
                message={photosLoadError}
                tone="warning"
                onRetry={retryLoadPhotos}
                className="mb-4"
              />
            )}

            {photosLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">{t.loading.photosLoading}</span>
              </div>
            ) : photosLoadError && filteredPhotosBySearch.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">{t.errors.loadFailed}</h3>
                <p className="text-gray-500 text-sm mb-4">{photosLoadError}</p>
                <button
                  type="button"
                  onClick={retryLoadPhotos}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {t.errors.retry}
                </button>
              </div>
            ) : filteredPhotosBySearch.length > 0 ? (
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <div className="divide-y divide-gray-200">
                  {filteredPhotosBySearch.map((photo: Photo, index: number) => (
                    <div
                      key={photo.key}
                      className={`group flex items-center justify-between p-4 transition-colors cursor-pointer ${
                        isBulkDeleteMode && selectedPhotosForBulkDelete.has(photo.key)
                          ? 'bg-red-50 hover:bg-red-100'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (isBulkDeleteMode) {
                          // Bulk delete mode - toggle selection on row click
                          togglePhotoForBulkDelete(photo.key);
                        } else if (!isComparisonMode) {
                          // Normal mode - open modal
                          setModalPhoto({
                            photos: [photo],
                            currentIndex: 0,
                            roomNumber: parseInt(photo.room?.toString() || '0')
                          })
                        }
                      }}
                    >
                      <div className="flex items-center space-x-4">
                        {/* Bulk Delete Mode Checkbox */}
                        {isBulkDeleteMode && (
                          <input
                            type="checkbox"
                            checked={selectedPhotosForBulkDelete.has(photo.key)}
                            onChange={(e) => {
                              e.stopPropagation();
                              togglePhotoForBulkDelete(photo.key);
                            }}
                            className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                          />
                        )}

                        {/* Comparison Mode Checkbox */}
                        {isComparisonMode && (
                          <input
                            type="checkbox"
                            checked={selectedPhotosForComparison.some(p => p.key === photo.key)}
                            onChange={(e) => {
                              e.stopPropagation();
                              togglePhotoSelection(photo);
                            }}
                            disabled={!selectedPhotosForComparison.some(p => p.key === photo.key) && selectedPhotosForComparison.length >= 2}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                        )}
                        
                        <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={photo.url}
                            alt={photo.originalName || `Fotoğraf ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {photo.originalName || (photo.key ? photo.key.split('/').pop()?.split('-').slice(2).join('-') || 'İsimsiz Fotoğraf' : 'İsimsiz Fotoğraf')}
                          </h3>
                          <div className="flex items-center space-x-3 mt-1">
                            {/* Photo Type Badge - Only show colorful badges */}
                            {(photo.blade || photo.category || photo.room) && (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                photo.blade ? 'bg-green-100 text-green-800' :
                                photo.category === 'drone' ? 'bg-blue-100 text-blue-800' :
                                photo.category === 'operator' ? 'bg-purple-100 text-purple-800' :
                                photo.category === 'robot' ? 'bg-orange-100 text-orange-800' :
                                photo.room ? 'bg-indigo-100 text-indigo-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {photo.blade ? `Kanat ${photo.blade}` :
                                 photo.category === 'drone' ? 'Drone' :
                                 photo.category === 'operator' ? 'Operatör' :
                                 photo.category === 'robot' ? 'Robot' :
                                 photo.room ? getRoomDisplayName(parseInt(photo.room.toString())) :
                                 'Diğer'}
                            </span>
                            )}
                            {photo.size && (
                            <span className="text-xs text-gray-400">
                              {(photo.size / 1024 / 1024).toFixed(1)} MB
                            </span>
                            )}
                          </div>
                          {photo.year && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-xs text-gray-400">
                                {photo.month ? getMonthName(photo.month, language) : ''} {photo.year}
                              </span>
                              {photo.uploadedBy && (
                                <span className="text-xs text-blue-600 font-medium">
                                  {photo.uploadedBy.name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {canDeletePhotos && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeletePhotoFromList(photo)
                            }}
                            className="opacity-0 group-hover:opacity-100 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                            title="Fotoğrafı sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-500 mb-2">Fotoğraf Bulunamadı</h3>
                <p className="text-gray-400 text-sm">
                  {searchTerm ? 'Arama kriterlerinize uygun fotoğraf bulunamadı.' : 'Bu türbin için henüz fotoğraf yüklenmemiş.'}
                </p>
              </div>
            )}
          </div>
    </>
  )
}

export default AllPhotosSection
